import { StatusChipVariant } from '@/components/ui/status-chip';
import type { DeletionState } from '@/hooks/useTenantDeletionMutations';

/**
 * Status Chip Utility - Maps status values to StatusChip variants
 *
 * This utility provides consistent mapping from semantic status values
 * to StatusChip visual variants across all entity types in the application.
 *
 * Variant Mapping:
 * - info (Blue): Positive/active states (Approved, Remediated, Completed, Online)
 * - high (Orange): Pending/warning states (Triaged, Pending, Partial)
 * - critical (Red): Negative/error states (Opened, Rejected, Failed, Offline)
 * - neutral (Green): Currently unused - reserved for future use
 * - muted (Gray): Inactive/frozen states (Frozen, Accepted, Deleted, Unknown, Queued, No scans)
 */

/**
 * Asset Status Variants
 * ('active' is the status code key; it renders as the "Approved" label)
 */
export const assetStatusVariants: Record<string, StatusChipVariant> = {
  active: 'info',
  frozen: 'muted',
  deleted: 'muted',
  pending: 'muted',
  unavailable: 'high',
  rejected: 'critical',
};

/**
 * Seed Status Variants
 */
export const seedStatusVariants: Record<string, StatusChipVariant> = {
  approved: 'info',
  pending: 'high',
  rejected: 'critical',
  frozen: 'muted',
  deleted: 'muted',
  dormant: 'muted',
};

/**
 * Vulnerability Status Variants
 */
export const vulnerabilityStatusVariants: Record<string, StatusChipVariant> = {
  triaged: 'high',
  opened: 'critical',
  remediated: 'info',
  accepted: 'muted',
  deleted: 'muted',
};

/**
 * Job/Scan Status Variants
 */
export const jobStatusVariants: Record<string, StatusChipVariant> = {
  completed: 'info',
  pass: 'info',
  failed: 'critical',
  fail: 'critical',
  running: 'info',
  queued: 'muted',
  partial: 'high',
  'no scans': 'muted',
};

/**
 * Agent Status Variants
 */
export const agentStatusVariants: Record<string, StatusChipVariant> = {
  online: 'info',
  offline: 'critical',
};

/**
 * Phishing Campaign Status Variants (Linear 10T-326)
 *
 * Keys are the derived UI statuses (`CampaignUiStatus`, incl. the client-only
 * `scheduled` derived from a draft with a future `schedule_start_at`). Values
 * come from the uiux-designer spec §3:
 * - live      → info  (teal-green pill — active/healthy)
 * - scheduled → high  (orange pill — pending/awaiting)
 * - completed → muted (gray pill — done/archival)
 * - draft     → muted (gray pill — not configured)
 * - paused    → high  (orange pill — attention/held; enum-ready, unused in v1)
 */
export const campaignStatusVariants: Record<string, StatusChipVariant> = {
  live: 'info',
  scheduled: 'high',
  completed: 'muted',
  draft: 'muted',
  paused: 'high',
};

/**
 * Tenant-Deletion Status Variants
 *
 * Maps every {@link DeletionState} to a StatusChip variant. Keyed on the
 * `DeletionState` union so the compiler enforces completeness — adding a new
 * deletion state to the union forces an entry here. In-flight states
 * (requested/enqueued/scheduled/purging/verifying) read as `high`
 * (attention/warning), `failed` as `critical`, and terminal-benign states
 * (completed/cancelled) as `muted`.
 */
export const deletionStatusVariants: Record<DeletionState, StatusChipVariant> =
  {
    requested: 'high',
    enqueued: 'high',
    scheduled: 'high',
    purging: 'high',
    verifying: 'high',
    completed: 'muted',
    failed: 'critical',
    cancelled: 'muted',
  };

/**
 * Get variant for a given status label (case-insensitive)
 */
export function getVariantForStatus(
  label: string,
  statusMap: Record<string, StatusChipVariant>
): StatusChipVariant {
  const normalizedLabel = label.toLowerCase();
  return statusMap[normalizedLabel] ?? 'default';
}
